/**
 * Product Listing Page - Browse, search, filter, and sort products
 */

import { ContentBlock, Box } from 'braid-design-system';
import type React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { Breadcrumbs } from '../../components/Breadcrumbs/Breadcrumbs';
import {
  ItemListing,
  type ListingViewMode,
} from '../../components/ItemListing/ItemListing';
import { PageHead } from '../../components/PageHead/PageHead';
import { buildItemListJsonLd } from '../../constants/jsonLd';
import { getCategories } from '../../data/productCatalog';
import { products } from '../../data/products';
import type { Product } from '../../types';

import * as styles from './ProductListing.css';

type SortOption = 'featured' | 'price-asc' | 'price-desc' | 'name' | 'rating';

const PAGE_SIZE = 9;

const sortProducts = (items: Product[], sort: SortOption): Product[] => {
  const sorted = [...items];

  switch (sort) {
    case 'price-asc':
      return sorted.sort((a, b) => a.price - b.price);
    case 'price-desc':
      return sorted.sort((a, b) => b.price - a.price);
    case 'name':
      return sorted.sort((a, b) => a.name.localeCompare(b.name));
    case 'rating':
      return sorted.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
    case 'featured':
    default:
      return sorted.sort((a, b) => {
        if (a.featured && !b.featured) {
          return -1;
        }
        if (!a.featured && b.featured) {
          return 1;
        }
        return 0;
      });
  }
};

export const ProductListing: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [allProducts] = useState<Product[]>(products);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState<SortOption>('featured');
  const [viewMode, setViewMode] = useState<ListingViewMode>('grid');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const categories = getCategories();

  useEffect(() => {
    const categoryFromUrl = searchParams.get('category') ?? 'all';
    const queryFromUrl = searchParams.get('q') ?? '';
    const sortFromUrl = (searchParams.get('sort') as SortOption) ?? 'featured';

    setSelectedCategory(categoryFromUrl);
    setSearchQuery(queryFromUrl);
    setSortOption(sortFromUrl);
    setVisibleCount(PAGE_SIZE);
  }, [searchParams]);

  const filteredProducts = useMemo(() => {
    let filtered = [...allProducts];

    if (selectedCategory !== 'all') {
      filtered = filtered.filter((p) => p.category === selectedCategory);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q),
      );
    }

    return sortProducts(filtered, sortOption);
  }, [allProducts, selectedCategory, searchQuery, sortOption]);

  const visibleProducts = filteredProducts.slice(0, visibleCount);

  const updateSearchParams = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '' || value === 'all') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });

    setSearchParams(next);
  };

  const handleCategoryChange = (category: string) => {
    updateSearchParams({ category: category === 'all' ? null : category });
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    updateSearchParams({ q: value || null });
  };

  const handleSortChange = (sort: SortOption) => {
    updateSearchParams({ sort: sort === 'featured' ? null : sort });
  };

  return (
    <ContentBlock width="large">
      <PageHead
        title="Products"
        description="Browse fairy doors, garden creatures, and accessories from Fae Forest."
        path="/products"
        jsonLd={buildItemListJsonLd(allProducts)}
      />
      <Box paddingY="large">
        <Breadcrumbs
          items={[{ label: 'Home', to: '/' }, { label: 'Products' }]}
        />

        <div className={styles.header}>
          <h1 className={styles.title}>Our Products</h1>
        </div>

        <ItemListing
          products={visibleProducts}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          showDescription
          meta={
            <p className={styles.resultCount}>
              Showing {visibleProducts.length} of {filteredProducts.length}{' '}
              product{filteredProducts.length !== 1 ? 's' : ''}
            </p>
          }
        >
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search products..."
            className={styles.searchInput}
            aria-label="Search products"
          />

          <select
            value={selectedCategory}
            onChange={(e) => handleCategoryChange(e.target.value)}
            className={styles.select}
            aria-label="Filter by category"
          >
            <option value="all">All Categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>

          <select
            value={sortOption}
            onChange={(e) => handleSortChange(e.target.value as SortOption)}
            className={styles.select}
            aria-label="Sort products"
          >
            <option value="featured">Featured</option>
            <option value="price-asc">Price: Low to High</option>
            <option value="price-desc">Price: High to Low</option>
            <option value="name">Name</option>
            <option value="rating">Rating</option>
          </select>
        </ItemListing>

        {visibleCount < filteredProducts.length && (
          <div className={styles.loadMoreContainer}>
            <button
              type="button"
              className={styles.loadMoreButton}
              onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
            >
              Load more
            </button>
          </div>
        )}
      </Box>
    </ContentBlock>
  );
};
